import React, { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShieldAlert, Check, X, MessageSquareWarning } from "lucide-react";

const REASON_LABEL = {
  spam: "Spam or promotional",
  harassment: "Harassment or abuse",
  fake_profile: "Fake or impersonation",
  inappropriate: "Inappropriate content",
  scam: "Scam / asking for money",
  other: "Other",
};

const STATUS_BADGE = {
  open: "bg-amber-50 text-amber-800 border-amber-300",
  reviewed: "bg-emerald-50 text-emerald-800 border-emerald-300",
  dismissed: "bg-slate-100 text-slate-700 border-slate-300",
};

export default function AdminReports() {
  const { user } = useAuth();
  const [filter, setFilter] = useState("open");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [detail, setDetail] = useState(null); // { item, decision }
  const [note, setNote] = useState("");

  const load = () => {
    setLoading(true);
    const q = filter === "all" ? "" : `?status=${filter}`;
    api.get(`/admin/reports${q}`)
      .then((r) => setItems(r.data.items))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (user?.role === "admin") load(); /* eslint-disable-next-line */ }, [user?.role, filter]);

  const counts = useMemo(() => {
    const c = { open: 0, reviewed: 0, dismissed: 0 };
    items.forEach((r) => { if (c[r.status] !== undefined) c[r.status] += 1; });
    return c;
  }, [items]);

  if (user && user.role !== "admin") {
    return <div className="max-w-3xl mx-auto p-10"><Card className="p-8 text-center">Admin only.</Card></div>;
  }

  const submitDecision = async () => {
    if (!detail) return;
    setBusy(detail.item.id);
    try {
      await api.post(`/admin/reports/${detail.item.id}/resolve`, {
        status: detail.decision,
        admin_note: note,
      });
      toast.success(`Report ${detail.decision === "reviewed" ? "resolved" : "dismissed"}`);
      setDetail(null);
      setNote("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const filters = [
    { key: "open", label: `Open (${counts.open})` },
    { key: "reviewed", label: "Reviewed" },
    { key: "dismissed", label: "Dismissed" },
    { key: "all", label: "All" },
  ];

  return (
    <div className="max-w-6xl mx-auto px-4 py-10 space-y-8" data-testid="admin-reports-page">
      <div className="flex items-center gap-3">
        <ShieldAlert className="w-8 h-8 text-[#6B1D2F]" />
        <div>
          <h1 className="font-serif text-3xl font-semibold text-slate-900">User Reports</h1>
          <p className="text-slate-600">Review member-submitted abuse reports and mark them reviewed or dismissed.</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="report-filters">
        {filters.map((f) => (
          <Button
            key={f.key}
            data-testid={`filter-${f.key}`}
            variant={filter === f.key ? "default" : "outline"}
            className={filter === f.key ? "bg-[#6B1D2F] hover:bg-[#541624] text-white rounded-full" : "rounded-full"}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </Button>
        ))}
      </div>

      {loading ? (
        <Card className="p-10 text-center text-slate-500 border-[#EAE3D2] bg-white">Loading…</Card>
      ) : items.length === 0 ? (
        <Card className="p-10 text-center text-slate-600 border-[#EAE3D2] bg-white" data-testid="reports-empty">
          <MessageSquareWarning className="w-10 h-10 mx-auto text-[#6B1D2F]/40" />
          <p className="mt-3">No {filter === "all" ? "" : filter} reports right now.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {items.map((r) => (
            <Card key={r.id} className="p-5 border-[#EAE3D2] bg-white" data-testid={`admin-report-${r.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={STATUS_BADGE[r.status] || STATUS_BADGE.open}>{r.status}</Badge>
                    <Badge className="bg-[#6B1D2F]/10 text-[#6B1D2F] border-[#6B1D2F]/30">{REASON_LABEL[r.reason] || r.reason}</Badge>
                    <span className="text-xs text-slate-500">{new Date(r.created_at).toLocaleString()}</span>
                  </div>
                  <h3 className="mt-3 font-serif text-lg text-slate-900">
                    {r.reporter?.full_name || "Someone"} <span className="text-slate-500 font-sans text-sm">reported</span> {r.reported?.full_name || "a user"}
                  </h3>
                  <p className="text-xs text-slate-500">{r.reporter?.email} → {r.reported?.email}</p>
                  {r.note && (
                    <div className="mt-3 p-3 bg-[#FAF6F0] rounded-md text-sm text-slate-800 border border-[#EAE3D2]">
                      <span className="text-slate-500 text-xs uppercase tracking-wide">Reporter's note</span>
                      <p className="mt-1 whitespace-pre-wrap">{r.note}</p>
                    </div>
                  )}
                  {r.message && (
                    <div className="mt-3 p-3 bg-[#FDF7EE] rounded-md text-sm text-slate-800 border border-[#D4AF37]/40">
                      <span className="text-slate-500 text-xs uppercase tracking-wide">Linked message</span>
                      <p className="mt-1 whitespace-pre-wrap">{r.message.text}</p>
                    </div>
                  )}
                  {r.admin_note && (
                    <p className="mt-3 text-xs text-slate-500"><b>Admin note:</b> {r.admin_note}</p>
                  )}
                  {r.resolved_at && (
                    <p className="mt-1 text-xs text-slate-400">Resolved {new Date(r.resolved_at).toLocaleString()}</p>
                  )}
                </div>
                {r.status === "open" && (
                  <div className="flex gap-2 shrink-0">
                    <Button
                      data-testid={`resolve-${r.id}`}
                      disabled={busy === r.id}
                      onClick={() => { setDetail({ item: r, decision: "reviewed" }); setNote(""); }}
                      className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full"
                    >
                      <Check className="w-4 h-4 mr-1" />Mark reviewed
                    </Button>
                    <Button
                      data-testid={`dismiss-${r.id}`}
                      disabled={busy === r.id}
                      variant="outline"
                      onClick={() => { setDetail({ item: r, decision: "dismissed" }); setNote(""); }}
                      className="rounded-full"
                    >
                      <X className="w-4 h-4 mr-1" />Dismiss
                    </Button>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) { setDetail(null); setNote(""); } }}>
        <DialogContent data-testid="resolve-dialog">
          <DialogHeader>
            <DialogTitle>
              {detail?.decision === "reviewed" ? "Mark report reviewed" : "Dismiss report"}
            </DialogTitle>
            <DialogDescription>
              {detail?.decision === "reviewed"
                ? "Add an optional note about the action taken (e.g. warned user, plan cancelled)."
                : "Add an optional note explaining why this report was dismissed."}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Admin note (optional)…"
            rows={3}
            maxLength={1000}
            data-testid="admin-note-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDetail(null); setNote(""); }} data-testid="resolve-cancel">Cancel</Button>
            <Button
              onClick={submitDecision}
              disabled={!!busy}
              data-testid="resolve-confirm"
              className={detail?.decision === "reviewed"
                ? "bg-emerald-700 hover:bg-emerald-800 text-white"
                : "bg-[#6B1D2F] hover:bg-[#541624] text-white"}
            >
              {detail?.decision === "reviewed" ? "Mark reviewed" : "Dismiss"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
